import {
  Snowflake,
  Wifi,
  UtensilsCrossed,
  Bed,
  Flame,
  Waves,
  ShowerHead,
  Umbrella,
  Trees,
  Home,
  Zap,
  Droplets,
  Users,
  GlassWater,
  Armchair,
  Car,
  Fan,
  type LucideIcon,
} from "lucide-react";
import type { ComponentType } from "react";
import PicnicTableIcon from "@/components/icons/PicnicTableIcon";

export type AmenityIcon = LucideIcon | ComponentType<{ className?: string }>;
export interface Amenity {
  id: string;
  label: string;
  Icon: AmenityIcon;
}

export const AMENITY_CATALOG: Record<string, Amenity> = {
  ac: { id: "ac", label: "Air Conditioning", Icon: Snowflake },
  wifi: { id: "wifi", label: "Wi-Fi", Icon: Wifi },
  kitchenette: { id: "kitchenette", label: "Kitchenette", Icon: UtensilsCrossed },
  full_kitchen: { id: "full_kitchen", label: "Full Kitchen", Icon: UtensilsCrossed },
  bathroom_private: {
    id: "bathroom_private",
    label: "Private In-Cabin Bathroom",
    Icon: ShowerHead,
  },
  bed_full: { id: "bed_full", label: "4 Full Beds", Icon: Bed },
  bed_queen: { id: "bed_queen", label: "2 Queen Beds", Icon: Bed },
  firepit: { id: "firepit", label: "Fire Pit", Icon: Flame },
  bbq_pit: { id: "bbq_pit", label: "Barbecue Pit", Icon: Flame },
  hammock: { id: "hammock", label: "Hammock", Icon: Trees },
  porch: { id: "porch", label: "Porch", Icon: Home },
  picnic_table: { id: "picnic_table", label: "Picnic Table", Icon: PicnicTableIcon },
  waterfront: { id: "waterfront", label: "Waterfront", Icon: Waves },
  covered: { id: "covered", label: "Covered Shade", Icon: Umbrella },
  electricity: { id: "electricity", label: "Electricity", Icon: Zap },
  water: { id: "water", label: "Water", Icon: Droplets },
  seating_for_15: { id: "seating_for_15", label: "Seating for 15", Icon: Users },
  bar_top: { id: "bar_top", label: "Bar Top", Icon: GlassWater },
  entry_6: { id: "entry_6", label: "Entry for 6", Icon: Users },
  entry_4: { id: "entry_4", label: "Entry for 4", Icon: Users },
  covered_deck: { id: "covered_deck", label: "Covered Deck", Icon: Umbrella },
  seating: { id: "seating", label: "Comfortable Seating", Icon: Armchair },
  parking_2: { id: "parking_2", label: "Parking for 2 Cars", Icon: Car },
  fan: { id: "fan", label: "Fan", Icon: Fan },
  bathhouse: {
    id: "bathhouse",
    label: "Restroom & Bathhouse Access",
    Icon: ShowerHead,
  },
};

export interface AccommodationOption {
  id: string;
  label: string;
  amenityIds: string[];
}

// Son's Blue River Camp accommodations
export const ACCOMMODATION_OPTIONS: AccommodationOption[] = [
  {
    id: "waterfront_cabin",
    label: "Waterfront Cabins",
    amenityIds: ["ac", "wifi", "bed_full", "firepit", "waterfront"],
  },
  {
    id: "deluxe_safari",
    label: "Deluxe Safari Cabins",
    amenityIds: ["ac", "wifi", "full_kitchen", "bathroom_private", "bed_full", "firepit"],
  },
  {
    id: "glamping_cabin",
    label: "Glamping Cabins",
    amenityIds: [
      "ac",
      "wifi",
      "kitchenette",
      "bed_queen",
      "firepit",
      "bbq_pit",
      "hammock",
      "picnic_table",
      "porch",
      "parking_2",
    ],
  },
  {
    id: "bell_tent",
    label: "Bell Tents",
    amenityIds: ["bed_queen", "firepit", "covered"],
  },
  {
    id: "cabana",
    label: "Riverside Cabanas",
    amenityIds: [
      "waterfront",
      "covered",
      "picnic_table",
      "bbq_pit",
      "hammock",
      "seating",
      "bar_top",
      "fan",
      "electricity",
      "water",
    ],
  },
  {
    id: "covered_picnic",
    label: "Riverside Covered Picnic Tables",
    amenityIds: [
      "entry_6",
      "picnic_table",
      "bbq_pit",
      "hammock",
      "seating",
      "bathhouse",
    ],
  },
];

const MAP = Object.fromEntries(ACCOMMODATION_OPTIONS.map((o) => [o.id, o]));

export function parseAccommodationIds(csv: string): string[] {
  return csv
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && MAP[s]);
}

export function amenitiesForAccommodations(accIds: string[]): Amenity[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const id of accIds) {
    const acc = MAP[id];
    if (!acc) continue;
    for (const aid of acc.amenityIds)
      if (!seen.has(aid)) {
        seen.add(aid);
        ordered.push(aid);
      }
  }
  return ordered.map((id) => AMENITY_CATALOG[id]).filter(Boolean);
}